import React, { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Input, Row, Col, Button, message } from "antd";
import { CloseOutlined, PlusOutlined, MinusOutlined } from "@ant-design/icons";
import DeleteIcon from "../../assets/images/delete1.svg";
import Refresh from "../../assets/images/refresh.svg";
import CancelArrow from "../../assets/images/cancelArrow.svg";
import { v4 as uuidv4 } from "uuid";
import BarCodeHandler from "../BarcodeHandler";
import { Scrollbars } from "react-custom-scrollbars";
import Axios from "axios";
import { stock } from "../../../src/components/PointOfSale/Core/stock.js";
import messages from "../../errors/messages.en-IN.json";
import db from "../../database";
import { useEventBus } from "../../lib/hooks/EventBusProvider";

const truncateToPrecision = (number, precision = 3) => {
  const epsilon = 1e-10;
  const factor = Math.pow(10, precision);
  return (Math.trunc(number * factor + epsilon) / factor).toFixed(precision);
};

const ProductsPopup = (props) => {
  const {
    productData,
    visibleProductsModal,
    setVisibleProductsModal,
    selectedItems,
    setSelectedItems,
    onSubmit,
    selectedProduct,
    setSelectedProduct,
    selectedProductCallback,
    weightPopup,
    setWeightPopup,
    selectedProductInCart,
    setSelectedProductInCart,
    cart,
    setCart,
    setProductStock,
  } = props;

  const { dispatchWeight } = useEventBus();
  const posConfig = JSON.parse(localStorage.getItem("posConfig"));
  const isAdvanceOrderEnabled = localStorage.getItem("isAdvanceOrderEnabled") === "true";
  const itemsPerPage = 10;

  const tillData = JSON.parse(localStorage.getItem("tillData"));
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showProductSelection, setShowProductSelection] = React.useState(false);
  const [isFirstInput, setIsFirstInput] = useState(true);
  const [unit, setUnit] = useState("kg");
  const [hasDecimal, setHasDecimal] = useState(false);
  const weightInputRef = useRef(null);

  const [uomPopup, setUomPopup] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [selectedUomId, setSelectedUomId] = useState(null);
  const [uomQuantity, setUomQuantity] = useState("0");
  const [isUomLocked, setIsUomLocked] = useState(false);
  const uomInputRef = useRef(null);
  const uomConfirmedRef = useRef(false);

  // ✅ FIX: Refs so interval callbacks always read the latest values (not stale closures)
  const availableUomsRef = useRef([]);
  const selectedUomIdRef = useRef(null);
  const uomPopupRef = useRef(false);
  const weightPopupRef = useRef(false);

  // ✅ FIX: Keep ALL refs always in sync with their state counterparts
  useEffect(() => {
    selectedUomIdRef.current = selectedUomId;
  }, [selectedUomId]);

  useEffect(() => {
    uomPopupRef.current = uomPopup;
  }, [uomPopup]);

  useEffect(() => {
    weightPopupRef.current = weightPopup;
    if (weightPopup) {
      setUomPopup(false);
    }
  }, [weightPopup]);

  useEffect(() => {
    if (uomConfirmedRef.current) {
      uomConfirmedRef.current = false;
      return;
    }

    if (selectedProduct?.alternateUom?.length > 0 && Array.isArray(selectedProduct?.alternateUom) && selectedProduct?.showAlternateUomInPos === "Y") {
      setPendingProduct(selectedProduct);
      console.log("Opening UOM popup for product:", selectedProduct);
      setUomPopup(true);
      setWeightPopup(false);
    } else {
      setUomPopup(false);
    }
  }, [selectedProduct]);

  const availableUoms = useMemo(() => {
    if (!pendingProduct) return [];

    const uoms = [];
    const addedUomIds = new Set();

    if (pendingProduct.uomData && pendingProduct.uomData.length > 0) {
      const primaryUom = pendingProduct.uomData[0];
      uoms.push({
        csUomId: primaryUom.csUomId,
        name: primaryUom.name,
        ediCode: primaryUom.ediCode || primaryUom.symbol,
        isPrimary: true,
        decimal: primaryUom.decimal,
        stdprecision: primaryUom.stdprecision,
        convertionRate: 1,
      });
      addedUomIds.add(primaryUom.csUomId);
    }

    if (pendingProduct.alternateUom && pendingProduct.alternateUom.length > 0) {
      pendingProduct.alternateUom.forEach((altUom) => {
        if (addedUomIds.has(altUom.csUomId)) {
          return;
        }
        uoms.push({
          csUomId: altUom.csUomId,
          name: altUom.name,
          ediCode: altUom.ediCode,
          isPrimary: false,
          convertionRate: altUom.convertionRate,
          decimal: "N",
          stdprecision: 0,
        });
        addedUomIds.add(altUom.csUomId);
      });
    }

    return uoms;
  }, [pendingProduct]);

  // ✅ FIX: Keep availableUomsRef always in sync with the latest computed availableUoms
  useEffect(() => {
    availableUomsRef.current = availableUoms;
  }, [availableUoms]);

  const selectedUom = useMemo(() => {
    return availableUoms.find((uom) => uom.csUomId === selectedUomId);
  }, [availableUoms, selectedUomId]);

  const uomPrice = useMemo(() => {
    if (!pendingProduct || !selectedUomId) {
      return pendingProduct?.sunitprice || 0;
    }

    const basePrice = pendingProduct.sunitprice || 0;

    if (Array.isArray(pendingProduct.priceList) && pendingProduct.priceList.length > 0) {
      const matchedPrice = pendingProduct.priceList.find((p) => p.uomId === selectedUomId);
      if (matchedPrice) {
        return matchedPrice.pricestd || matchedPrice.pricelist || basePrice;
      }
    }

    const selectedUomObj = availableUoms.find((uom) => uom.csUomId === selectedUomId);
    if (selectedUomObj && !selectedUomObj.isPrimary && selectedUomObj.convertionRate) {
      return basePrice * Number(selectedUomObj.convertionRate);
    }

    return basePrice;
  }, [pendingProduct, selectedUomId, availableUoms]);

  const uomTotalPrice = useMemo(() => {
    const qty = Number.parseFloat(uomQuantity) || 0;
    return (uomPrice * qty).toFixed(2);
  }, [uomPrice, uomQuantity]);

  // useEffect(() => {
  //   if (uomPopup && pendingProduct) {
  //     setIsUomLocked(false);
  //     const primaryUomId = pendingProduct.uom || pendingProduct.uomData?.[0]?.csUomId;
  //     setSelectedUomId(primaryUomId);
  //     setIsFirstInput(true);
  //   }
  // }, [uomPopup, pendingProduct]);

useEffect(() => {
  if (uomPopup && pendingProduct) {
    setIsUomLocked(false);
    const selectedId =
      pendingProduct.uom || pendingProduct.uomData?.[0]?.csUomId;
    setSelectedUomId(selectedId);
    setIsFirstInput(true);
    // ✅ preload existing cart qty
    const existingQty =
      pendingProduct.rawWeight ??
      pendingProduct.weight ??
      "0";
    const selectedUomObj = availableUoms.find(
      (u) => u.csUomId === selectedId
    );
    const isKg = selectedUomObj?.decimal === "Y";
    setUomQuantity(
      existingQty && Number(existingQty) > 0
        ? existingQty.toString()
        : isKg
        ? "0"
        : "1"
    );
  }
}, [uomPopup, pendingProduct, availableUoms]);


  useEffect(() => {
    if (uomPopup && uomInputRef.current) {
      setTimeout(() => {
        uomInputRef.current?.focus();
        uomInputRef.current?.select();
      }, 100);
    }
  }, [uomPopup, selectedUomId]);

  useEffect(() => {
    if (!weightPopup) return;

    const focusInput = () => {
      if (weightInputRef.current) {
        weightInputRef.current.focus();
      }
    };

    focusInput();
    const timeout = setTimeout(focusInput, 150);
    return () => clearTimeout(timeout);
  }, [weightPopup, selectedProduct.rawWeight]);

  const handleUomInputChange = (value) => {
    // if (isUomLocked || (selectedProduct?.restrictPosQtyEdit === "Y" && selectedUom?.name?.toLowerCase() === "kilogram")) return;
    if (isUomLocked || (!isAdvanceOrderEnabled && selectedProduct?.restrictPosQtyEdit === "Y" && selectedUom?.name?.toLowerCase() === "kilogram")) return;
    const isDecimal = selectedUom?.decimal === "Y";
    let sanitizedValue = value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

    // ✅ FIX: Strip decimals for non-decimal UOMs (Pieces must always be whole numbers)
    if (!isDecimal) {
      sanitizedValue = sanitizedValue.replace(/\./g, "");
    }

    setUomQuantity(sanitizedValue === "" ? "0" : sanitizedValue);
    setIsFirstInput(false);
  };

  const handleUomConfirm = async () => {
    console.log("handleuomconfirm");
    const qty = Number.parseFloat(uomQuantity) || 1;

    if (qty <= 0) {
      message.error("Quantity must be greater than 0");
      return;
    }

    let newUomData = pendingProduct.uomData;

    if (selectedUomId) {
      try {
        const uomFromDb = await db.productUom.where("csUomId").equals(selectedUomId).toArray();
        if (uomFromDb && uomFromDb.length > 0) {
          newUomData = uomFromDb;
        }
      } catch (error) {
        console.error("Error fetching uomData from db:", error);
        newUomData = pendingProduct.uomData;
      }
    }

    const updatedProduct = {
      ...pendingProduct,
      uom: selectedUomId,
      uom_name: selectedUom?.name,
      uomData: newUomData,
      weight: qty,
      isQtyDesimal: newUomData[0]?.stdprecision ?? 2,
      rawWeight: uomQuantity,
      unit: selectedUom?.name?.toLowerCase() || pendingProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
      sunitprice: uomPrice,
      price: uomPrice,
      salePrice: uomPrice,
      realPrice: uomPrice,
    };

    uomConfirmedRef.current = true;

    setIsUomLocked(false);
    setUomPopup(false);
    setPendingProduct(null);
    setSelectedUomId(null);
    setUomQuantity("0");
    selectedProductCallback(updatedProduct);
    setSelectedProduct(updatedProduct);
  };

  const handleUomCancel = () => {
    resetModalState();
  };

  const getUomIcon = (uomName) => {
    const name = uomName?.toLowerCase() || "";
    if (name.includes("piece") || name.includes("pcs") || name.includes("nos") || name.includes("unit")) {
      return (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
      );
    }
    return (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 3v18" />
        <path d="M8 6l4-3 4 3" />
        <rect x="4" y="10" width="16" height="10" rx="1" />
        <path d="M8 15h8" />
        <path d="M8 18h8" />
      </svg>
    );
  };

  useEffect(() => {
    let filtered = productData.filter((item) => {
      const lowerQuery = searchQuery.toLowerCase();
      const isValidSegment = item.productSegment !== "CB";
      const matchesSearch =
        item.name.toLowerCase().includes(lowerQuery) || item.value.toLowerCase().includes(lowerQuery) || (item.upc && item.upc.toLowerCase().includes(lowerQuery));
      return isValidSegment && matchesSearch;
    });

    setFilteredProducts(filtered);
  }, [searchQuery, productData, selectedItems]);

  useEffect(() => {
    if (weightPopup) {
      setIsFirstInput(true);
      setHasDecimal(false);
      if (!selectedProduct.weight) {
        setSelectedProduct({
          ...selectedProduct,
          weight: "0",
          rawWeight: "0",
        });
      }
      if (posConfig?.predefined_weight_buttons === "Y" && !unit) {
        setUnit(selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg");
      }
    }
  }, [weightPopup, selectedProduct]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, currentPage]);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const needsPopup = (product) =>
    !!((product?.alternateUom?.length > 0 && product?.showAlternateUomInPos === "Y") || product?.isManualQty === true || product?.showWeightPopup === "Y");

  const openWeightPopupForItem = (product, callback) => {
    const existing = selectedItems.find((i) => i.mProductId === product.mProductId);
    dispatchWeight(
      {
        ...product,
        weight: existing?.weight || product.weight || 0,
        rawWeight: existing?.rawWeight || product.rawWeight || product.weight?.toString() || "",
      },
      () => {},
      null,
      () => {},
      (updatedProduct) => {
        if (!updatedProduct) return;
        callback(updatedProduct);
      }
    );
  };

  const handleIncrease = async (product) => {
    const isInCart = selectedItems.find((item) => item.mProductId === product.mProductId && item.uom === (product.uom || product.csUomId));
    const prevSelectedItems = [...selectedItems];
    const updatedSelectedItems = isInCart
      ? selectedItems.map((item) =>
          item.mProductId === product.mProductId
            ? {
                ...item,
                weight: Number(item.weight) + 1,
                rawWeight: (Number(item.weight) + 1).toString(),
                unit: unit || product?.uomData?.[0]?.name?.toLowerCase() || "kg",
              }
            : item
        )
      : [
          ...selectedItems,
          {
            ...product,
            weight: 1,
            rawWeight: "1",
            uom: product.uom || product.csUomId,
            unit: unit || product?.uomData?.[0]?.name?.toLowerCase() || "kg",
            lineId: uuidv4().replace(/-/g, "").toUpperCase(),
          },
        ];
    const tempCart = { ...cart, items: updatedSelectedItems };
    const isPreOrderPage = window.location.pathname.includes("/preorder");
    if (navigator.onLine && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y" && !isPreOrderPage) {
      const stockCheckResult = await stock({
        cart: tempCart,
        setCart,
        tillData,
        setProductStock,
        isFinalValidation: false,
      });
      if (!stockCheckResult) {
        setSelectedItems(prevSelectedItems);
        return;
      }
    }
    setSelectedItems(updatedSelectedItems);
  };

  const handleDecrease = (product) => {
    const isInCart = selectedItems.find((item) => item.mProductId === product.mProductId && item.uom === (product.uom || product.csUomId));
    if (isInCart && isInCart.weight > 1) {
      setSelectedItems(
        selectedItems.map((item) =>
          item.mProductId === product.mProductId
            ? {
                ...item,
                weight: Number(item.weight) - 1,
                rawWeight: (Number(item.weight) - 1).toString(),
                unit: item.unit || unit || product?.uomData?.[0]?.name?.toLowerCase() || "kg",
              }
            : item
        )
      );
    } else if (isInCart && isInCart.weight <= 1) {
      setSelectedItems(selectedItems.filter((item) => item.mProductId !== product.mProductId));
    }
  };

  const handleQuantityChange = async (product, value) => {
    const isDecimal = product?.uomData?.[0]?.decimal === "Y";
    let newValue = value;

    if (newValue === "") {
      newValue = "0";
    }

    const currentWeight = product.rawWeight || (product.weight !== undefined ? product.weight.toString() : "0");
    const isInitialZero = currentWeight === "0";
    if (isInitialZero && /\d/.test(newValue) && !newValue.startsWith("0.")) {
      newValue = newValue.replace(/^0+/, "");
      if (newValue === "") newValue = "0";
    }

    const isValidInput = isDecimal ? /^(\d*\.?\d{0,3}|\d*\.|\d*\.\d{0,3}\.)$/.test(newValue) : /^\d*$/.test(newValue);
    if (!isValidInput) return;

    const parsedValue = newValue === "0" || newValue === "." || newValue === "0." || newValue === "0.0" || newValue === "0.00" ? 0 : parseFloat(newValue) || 0;

    if (parsedValue < 0) {
      message.error(`${messages.info.quantity_not_negative}`);
      return;
    }

    const isInCart = selectedItems.find((item) => item.mProductId === product.mProductId && item.uom === (product.uom || product.csUomId));
    const prevSelectedItems = [...selectedItems];
    let updatedSelectedItems = [];

    if (isInCart) {
      if (parsedValue === 0 && ![".", "0.", "0.0", "0.00"].includes(newValue)) {
        updatedSelectedItems = selectedItems.filter((item) => item.mProductId !== product.mProductId);
      } else {
        updatedSelectedItems = selectedItems.map((item) =>
          item.mProductId === product.mProductId
            ? {
                ...item,
                weight: isDecimal ? parsedValue : Math.floor(parsedValue),
                rawWeight: newValue,
                unit: item.unit || unit || product?.uomData?.[0]?.name?.toLowerCase() || "kg",
              }
            : item
        );
      }
    } else if (parsedValue > 0 || [".", "0.", "0.0", "0.00"].includes(newValue)) {
      updatedSelectedItems = [
        ...selectedItems,
        {
          ...product,
          weight: isDecimal ? parsedValue : Math.floor(parsedValue),
          rawWeight: newValue,
          uom: product.uom || product.csUomId,
          unit: unit || product?.uomData?.[0]?.name?.toLowerCase() || "kg",
          lineId: uuidv4().replace(/-/g, "").toUpperCase(),
        },
      ];
    } else {
      return;
    }

    const tempCart = { ...cart, items: updatedSelectedItems };
    const isPreOrderPage = window.location.pathname.includes("/preorder");
    if (navigator.onLine && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y" && !isPreOrderPage) {
      const stockCheckResult = await stock({
        cart: tempCart,
        setCart,
        tillData,
        setProductStock,
        isFinalValidation: false,
      });
      if (!stockCheckResult) {
        setSelectedItems(prevSelectedItems);
        return;
      }
    }
    setSelectedItems(updatedSelectedItems);
  };

  const handleDelete = (itemToDelete) => {
    setSelectedItems(selectedItems.filter((item) => item.lineId !== itemToDelete.lineId));
  };

  const resetModalState = () => {
    setSelectedItems([]);
    setSearchQuery("");
    setCurrentPage(0);
    setIsUomLocked(false);
    setShowProductSelection(false);
    setSelectedProduct({});
    setWeightPopup(false);
    setUomPopup(false);
    setPendingProduct(null);
    setSelectedUomId(null);
    setUomQuantity("0");
  };

  const handleSubmit = () => {
    const updatedItems = selectedItems.map((item) => ({
      ...item,
      weight: Number(item.weight),
      rawWeight: item.rawWeight || item.weight.toString(),
      unit: item.unit || unit || item?.uomData?.[0]?.name?.toLowerCase() || "kg",
    }));
    onSubmit(updatedItems);
    setVisibleProductsModal(false);
    resetModalState();
  };

  const handleTotalQty = (value) => {
    if (!isAdvanceOrderEnabled && selectedProduct?.restrictPosQtyEdit === "Y" && weightPopup) {
      return;
    }
    if (Object.keys(selectedProduct).length === 0 || selectedProduct.isReturn) return;

    let newWeight = selectedProduct.weight ? selectedProduct.weight.toString() : "0";
    const isPredefinedButtonsEnabled = posConfig?.predefined_weight_buttons === "Y";
    let displayWeight = isPredefinedButtonsEnabled && unit === "g" ? (Number(newWeight) * 1000).toString() : newWeight;
    const currentHasDecimal = displayWeight.includes(".");

    switch (value) {
      case "x":
        displayWeight = displayWeight.slice(0, -1) || "0";
        setHasDecimal(displayWeight.includes("."));
        break;
      case "clear":
        displayWeight = "0";
        setIsFirstInput(true);
        setHasDecimal(false);
        break;
      case ".":
        if (!currentHasDecimal && displayWeight.length !== "0" && (unit === "kg" || !isPredefinedButtonsEnabled)) {
          displayWeight = `${displayWeight}.`;
          setHasDecimal(true);
        }
        break;
      default:
        if (isFirstInput && displayWeight === "0") {
          displayWeight = value;
          setIsFirstInput(false);
        } else {
          displayWeight = `${displayWeight}${value}`;
        }
        setHasDecimal(displayWeight.includes("."));
        break;
    }

    newWeight = isPredefinedButtonsEnabled && unit === "g" ? (Number(displayWeight) / 1000).toString() : displayWeight;

    setSelectedProduct({
      ...selectedProduct,
      weight: newWeight,
      rawWeight: displayWeight,
      unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
    });
  };

  const handleInputChange = (value) => {
    // if (selectedProduct?.restrictPosQtyEdit === "Y" && weightPopup) {
    if (!isAdvanceOrderEnabled && selectedProduct?.restrictPosQtyEdit === "Y" && weightPopup) {
      return;
    }
    if (Object.keys(selectedProduct).length === 0) return;

    const isPredefinedButtonsEnabled = posConfig?.predefined_weight_buttons === "Y";
    const isDecimal = selectedProduct?.uomData?.[0]?.decimal === "Y";
    let sanitizedValue = value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    if (isPredefinedButtonsEnabled && unit === "g" && !isDecimal) {
      sanitizedValue = sanitizedValue.replace(/\./g, "");
    }
    let newWeight = sanitizedValue === "" ? "0" : sanitizedValue;
    if (isPredefinedButtonsEnabled && unit === "g") {
      newWeight = (Number(sanitizedValue) / 1000).toString();
    }

    setSelectedProduct({
      ...selectedProduct,
      weight: newWeight,
      rawWeight: sanitizedValue,
      unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
    });
    setHasDecimal(newWeight.includes("."));
    setIsFirstInput(false);
  };

  const callGetWeightAPI = async (productDefined) => {
    if (weightPopup && !isAdvanceOrderEnabled) {
      setIsUomLocked(false);
    }
    // Read ALL live values from refs — these are always current, never stale
    const liveUomId = selectedUomIdRef.current;
    const liveUoms = availableUomsRef.current;
    const liveUomPopup = uomPopupRef.current;
    const liveWeightPopup = weightPopupRef.current;

    const currentUom = liveUoms.find((uom) => uom.csUomId === liveUomId);
    // isKgUom is true ONLY when decimal === "Y" (Kilogram), false for Pieces/any non-decimal
    const isKgUom = currentUom?.decimal === "Y";

    console.log("[WeightAPI] liveUomPopup:", liveUomPopup, "liveWeightPopup:", liveWeightPopup, "liveUomId:", liveUomId, "isKgUom:", isKgUom);

    try {
      const response = await Axios({
        url: `${tillData?.tillAccess?.cwrTill?.hardwareController?.imageUrl}getWeight`,
        method: "POST",
      });

      const productToUpdate = productDefined && Object.keys(productDefined)?.length > 0 ? productDefined : selectedProductInCart;

      if (response?.data?.Weight) {
        const weight = parseFloat(response.data.Weight.replace("kg", "").trim());
        if (!isNaN(weight)) {
          if (uomPopup && weight > 0) {
            setIsUomLocked(true);
          } else if (uomPopup && weight === 0) {
            setIsUomLocked(false);
          }
          // CASE 1: Weight popup only (no UOM popup open) — always write weight
          if (liveWeightPopup && !liveUomPopup) {
            if (weight !== parseFloat(selectedProduct.weight)) {
              productToUpdate.weight = parseFloat(truncateToPrecision(weight, currentUom?.stdprecision));
              productToUpdate.rawWeight = parseFloat(truncateToPrecision(weight.toString(), currentUom?.stdprecision));
              productToUpdate.unit = unit || productToUpdate?.uomData?.[0]?.name?.toLowerCase() || "kg";
              setSelectedProductInCart({ ...productToUpdate });
              const updatedProduct = {
                ...selectedProduct,
                weight: parseFloat(truncateToPrecision(weight, currentUom?.stdprecision)),
                rawWeight: truncateToPrecision(weight, currentUom?.stdprecision),
                unit: unit || productToUpdate?.uomData?.[0]?.name?.toLowerCase() || "kg",
              };
              setSelectedProduct(updatedProduct);
              setHasDecimal(weight.toString().includes("."));
            }
            return;
          }

          // CASE 2: UOM popup is open
          if (liveUomPopup) {
            // HARD STOP: Pieces or any non-decimal UOM selected — NEVER write scale weight
            if (!isKgUom) {
              console.log("[WeightAPI] Pieces selected — blocking scale weight write");
              return;
            }
            // Only reaches here when Kilogram is selected
            console.log("[WeightAPI] KG selected — writing weight:", weight);
            const precision = currentUom?.decimal === "Y" ? 3 : 0;
            setUomQuantity(truncateToPrecision(weight, precision));
            //setUomQuantity(truncateToPrecision(weight, currentUom?.stdprecision));
            return;
          }
        }
      }
    } catch (error) {
      console.error("Error in callGetWeightAPI:", error);
    }
  };

  useEffect(() => {
    if (weightPopup) {
      setIsUomLocked(false);
    }
  }, [weightPopup, uomPopup]);

  // useEffect(() => {
  //   let interval;

  //   // Use refs for current live values at the time this effect runs
  //   const liveUomId = selectedUomIdRef.current;
  //   const liveUoms = availableUomsRef.current;
  //   const currentUom = liveUoms.find((uom) => uom.csUomId === liveUomId);
  //   const isKgUom = currentUom?.decimal === "Y";

  //   // Only poll if: weight popup is open OR (uom popup is open AND kilogram is selected)
  //   // const shouldPoll = weightPopup || (uomPopup && isKgUom);
  //   console.log("EFFECT RUN:", {
  //     weightPopup,
  //     uomPopup,
  //     selectedUomId,
  //     availableUoms: availableUomsRef.current,
  //     isUomLocked,
  //     isKgUom,
  //   });
  //   if (weightPopup) {
  //     console.log("Weight popup open — allowing scale weight writes");
  //     setIsUomLocked(false);
  //   }
  //   const shouldPoll = !isUomLocked && (weightPopup || (uomPopup && isKgUom));

  //   // if (uomPopup && isKgUom) {
  //   //   callGetWeightAPI(selectedProduct);
  //   //   return;
  //   // }

  //   if (shouldPoll) {
  //     console.log("🚀 Starting Weight API polling");
  //     callGetWeightAPI(selectedProduct);
  //     interval = setInterval(() => {
  //       callGetWeightAPI(selectedProduct);
  //     }, 1000);
  //   }

  //   return () => clearInterval(interval);
  //   // All three in deps: restarts interval whenever any of these change
  // }, [weightPopup, selectedUomId]);

  useEffect(() => {
    let interval;

    const liveUomId = selectedUomIdRef.current;
    const liveUoms = availableUomsRef.current;

    const currentUom = liveUoms.find((uom) => uom.csUomId === liveUomId);
    const isKgUom = currentUom?.decimal === "Y";

    const shouldPoll = !isUomLocked && (weightPopup || (uomPopup && isKgUom));

    if (shouldPoll) {
      callGetWeightAPI(selectedProduct);

      interval = setInterval(() => {
        callGetWeightAPI(selectedProduct);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [
    weightPopup,
    uomPopup, // ✅ ADD THIS
    selectedUomId,
    availableUoms, // ✅ ADD THIS (important)
  ]);

  const frequentWeights = unit === "kg" ? [0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0, 2.5, 3.0, 4.0, 5.0] : [250, 500, 750, 1000, 1250, 1500, 1750, 2000, 2500, 3000, 4000, 5000];

  const handleFrequentWeight = (weight) => {
    const weightInKg = unit === "g" ? weight / 1000 : weight;
    setSelectedProduct({
      ...selectedProduct,
      weight: weightInKg.toString(),
      rawWeight: unit === "g" ? weight.toString() : weightInKg.toString(),
      unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
    });
    setHasDecimal(weightInKg.toString().includes("."));
  };

  const isStrictQty = posConfig?.requireQtyBeforeAddingItem === "Y";
  const isZeroWeight = Number(selectedProduct?.weight || 0) <= 0;

  return (
    <>
      <Modal
        visible={uomPopup}
        getContainer={false}
        zIndex={2000}
        closable={false}
        onCancel={handleUomCancel}
        keyboard={false}
        footer={null}
        width="400px"
        centered
        bodyStyle={{ padding: "24px", backgroundColor: "#fff" }}
        title={null}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Header */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#000" }}>Select Unit for {pendingProduct?.name}</h3>

              {/* {selectedUom?.decimal === "Y" && <img src={Refresh} style={{ cursor: "pointer" }} onClick={() => callGetWeightAPI(selectedProduct)} />} */}
            </div>

            <p style={{ margin: "4px 0 0 0", fontSize: "14px", color: "#666" }}>Choose whether to sell by pieces or weight</p>
          </div>

          {/* UOM Selection Cards */}
          <div style={{ display: "flex", gap: "12px" }}>
            {availableUoms.map((uom) => (
              <div
                key={uom.csUomId}
                onClick={() => {
                  if (isUomLocked) return;
                  setSelectedUomId(uom.csUomId);
                  const isKg = uom?.decimal === "Y";
                  if (!isKg) {
                    // ✅ Pieces selected — always reset to whole number "1"
                    setUomQuantity("1");
                  } else {
                    // KG selected — reset to "0" so scale can populate it
                    setUomQuantity("0");
                  }
                }}
                style={{
                  flex: 1,
                  padding: "16px",
                  border: selectedUomId === uom.csUomId ? "2px solid #000" : "1px solid #e0e0e0",
                  borderRadius: "8px",
                  cursor: isUomLocked && uom?.decimal !== "Y" ? "not-allowed" : "pointer",
                  backgroundColor: selectedUomId === uom.csUomId ? "#000" : "#fff",
                  color: selectedUomId === uom.csUomId ? "#fff" : "#000",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s ease",
                }}
              >
                {getUomIcon(uom.name)}
                <span style={{ fontSize: "14px", fontWeight: 500 }}>{uom.name}</span>
              </div>
            ))}
          </div>

          {/* Quantity Input */}
          <div>
            <label
              style={{
                display: "flex",
                justifyContent: "space-between", // 👈 key
                alignItems: "center",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#000",
              }}
            >
              <span>Quantity ({selectedUom?.ediCode || selectedUom?.name || "units"})</span>

              {posConfig?.showStoreSpecificStock === "Y" && <span>Stock: {selectedProduct?.stock ?? 0}</span>}
            </label>
            <Input
              ref={uomInputRef}
              type="text"
              value={uomQuantity}
              onChange={(e) => handleUomInputChange(e.target.value)}
              disabled={!isAdvanceOrderEnabled && selectedProduct?.restrictPosQtyEdit === "Y" && selectedUom?.name?.toLowerCase() === "kilogram"}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (isStrictQty && Number(uomQuantity) <= 0) return;
                  handleUomConfirm();
                }
                // ✅ FIX: Block decimal key for non-decimal UOMs at keyboard level too
                const isDecimal = selectedUom?.decimal === "Y";
                if (!isDecimal && e.key === ".") {
                  e.preventDefault();
                }
              }}
              style={{
                width: "100%",
                height: "48px",
                fontSize: "16px",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                color:"#000",
              }}
            />
          </div>

          {/* Total Price */}
          <div
            style={{
              backgroundColor: "#f5f5f5",
              padding: "16px",
              borderRadius: "8px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: "14px", color: "#666" }}>Total Price</span>
            <span style={{ fontSize: "24px", fontWeight: 600, color: "#000" }}>₹{Number(uomTotalPrice).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: "12px", marginTop: "4px" }}>
            <Button
              onClick={handleUomCancel}
              style={{
                flex: 1,
                height: "48px",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                backgroundColor: "#fff",
                color: "#000",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUomConfirm}
              disabled={isStrictQty && Number(uomQuantity) <= 0}
              style={{
                flex: 1,
                height: "48px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#000",
                color: "#fff",
                fontSize: "14px",
                fontWeight: 500,
                cursor: isStrictQty && Number(uomQuantity) <= 0 ? "not-allowed" : "pointer",
              }}
            >
              Add to Cart
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        visible={visibleProductsModal}
        centered={true}
        width={"72%"}
        closable={true}
        closeIcon={
          <CloseOutlined
            style={{ color: "#2F3856", fontSize: "16px" }}
            onClick={() => {
              setVisibleProductsModal(false);
              localStorage.removeItem("productSegment");
              resetModalState();
            }}
          />
        }
        footer={null}
      >
        <BarCodeHandler setSelectedItems={setSelectedItems} />
        {showProductSelection ? (
          <div style={{ padding: "5vh 0 0 0", height: "85vh", position: "relative" }}>
            <Input
              placeholder="Search Item Code / Description"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(0);
              }}
              style={{
                borderRadius: "7px",
                padding: "0.8vh 0.8vw",
                border: "1px solid #d9d9d9",
                width: "100%",
              }}
            />

            <Row style={{ padding: "2vh 0" }} gutter={[16, 16]}>
              {paginatedData.length > 0 ? (
                paginatedData.map((item, index) => {
                  const cartItem = selectedItems.find((cartItem) => cartItem.mProductId === item.mProductId && cartItem.uom === (item.uom || item.csUomId));
                  const uomName = item?.uomData?.[0]?.name ?? "";
                  const quantity = cartItem ? cartItem.rawWeight || cartItem.weight.toString() : "0";
                  const isDecimal = item?.uomData?.[0]?.decimal === "Y";

                  return (
                    <Col key={index} span={12} style={{ padding: "0 8px" }}>
                      <Row style={{ padding: "1.5%", width: "100%", border: "1px solid #d9d9d9", height: "10vh" }}>
                        <Col span={12}>
                          <p
                            title={item.name}
                            style={{
                              margin: 0,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "100%",
                            }}
                          >
                            {item.name}
                          </p>
                          <p style={{ margin: 0 }}>
                            {item.sunitprice} - {uomName} {posConfig?.showStoreSpecificStock === "Y" && item.onhandQty !== undefined ? `- ${item.onhandQty ?? 0}` : ""}
                          </p>
                        </Col>
                        <Col span={12} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Button
                              onClick={() => {
                                if (needsPopup(item)) {
                                  openWeightPopupForItem(item, async (updatedProduct) => {
                                    const newValue = updatedProduct?.rawWeight || updatedProduct?.weight?.toString();
                                    await handleQuantityChange(updatedProduct, newValue);
                                  });
                                } else {
                                  handleDecrease(item);
                                }
                              }}
                              style={{ backgroundColor: "#2F3856", color: "#fff", width: "2.5vw", height: "4vh" }}
                              icon={<MinusOutlined />}
                            />
                            <Input
                              type="text"
                              value={quantity}
                              readOnly={needsPopup(item)}
                              onClick={() => {
                                if (needsPopup(item)) {
                                  openWeightPopupForItem(item, async (updatedProduct) => {
                                    const newValue = updatedProduct?.rawWeight || updatedProduct?.weight?.toString();
                                    await handleQuantityChange(updatedProduct, newValue);
                                  });
                                }
                              }}
                              onChange={(e) => {
                                if (!needsPopup(item)) {
                                  handleQuantityChange(item, e.target.value);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  const newValue = e.target.value || "0";
                                  handleQuantityChange(item, newValue);
                                } else if (!isDecimal && !/[\d]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                                  e.preventDefault();
                                } else if (isDecimal && !/[\d.]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              onBlur={(e) => {
                                const newValue = e.target.value || "0";
                                if (isDecimal && [".", "0.", "0.0", "0.00"].includes(newValue)) {
                                  handleQuantityChange(item, "0");
                                } else {
                                  handleQuantityChange(item, newValue);
                                }
                              }}
                              placeholder={isDecimal ? "e.g., 0.001" : "e.g., 1"}
                              style={{ width: "60px", height: "4vh", textAlign: "center" }}
                            />
                            <Button
                              onClick={() => {
                                if (needsPopup(item)) {
                                  openWeightPopupForItem(item, async (updatedProduct) => {
                                    let newValue = updatedProduct?.rawWeight ?? updatedProduct?.weight?.toString() ?? "";
                                    if (!newValue || newValue === "0") newValue = "1";
                                    await handleQuantityChange(updatedProduct, newValue);
                                  });
                                } else {
                                  handleIncrease(item);
                                }
                              }}
                              style={{ backgroundColor: "#2F3856", color: "#fff", width: "2.5vw", height: "4vh" }}
                              icon={<PlusOutlined />}
                            />
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  );
                })
              ) : (
                <p style={{ textAlign: "center", width: "100%" }}>No products found</p>
              )}
            </Row>

            <div style={{ position: "absolute", bottom: "0vh", width: "100%", padding: "1vh 0" }}>
              <Row align="middle" justify="space-between">
                <Col>
                  <Button
                    disabled={currentPage === 0}
                    onClick={handlePreviousPage}
                    style={{
                      textAlign: "center",
                      backgroundColor: currentPage === 0 ? "#ccc" : "#2F3856",
                      width: "4vw",
                      height: "6.5vh",
                      color: "#fff",
                      borderRadius: "5px",
                      marginRight: "1vw",
                    }}
                  >
                    {"<"}
                  </Button>
                  <span style={{ fontWeight: "bold", margin: "0 1vw" }}>
                    Page {currentPage + 1} of {totalPages || 1}
                  </span>
                  <Button
                    disabled={currentPage >= totalPages - 1}
                    onClick={handleNextPage}
                    style={{
                      textAlign: "center",
                      backgroundColor: currentPage >= totalPages - 1 ? "#ccc" : "#2F3856",
                      width: "4vw",
                      height: "6.5vh",
                      color: "#fff",
                      borderRadius: "5px",
                      marginLeft: "1vw",
                    }}
                  >
                    {">"}
                  </Button>
                </Col>
                <Col>
                  <Button
                    onClick={() => setShowProductSelection(false)}
                    style={{
                      backgroundColor: "#2F3856",
                      color: "#fff",
                      width: "8vw",
                      height: "6.5vh",
                      borderRadius: "5px",
                      marginRight: "1vw",
                    }}
                    disabled={selectedItems.length === 0}
                  >
                    Okay
                  </Button>
                </Col>
              </Row>
            </div>
          </div>
        ) : (
          <div style={{ height: "81vh", position: "relative" }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: "2vh", marginTop: "3vh" }}>
              <Col>
                <h3>{selectedProduct?.name || ""}</h3>
              </Col>
              <Col>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowProductSelection(true)} style={{ backgroundColor: "#2F3856", borderColor: "#2F3856" }}>
                  Add Products
                </Button>
              </Col>
            </Row>
            <Row
              style={{
                padding: "0.1rem 0.75rem",
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "#2F3856",
                borderBottom: "1px solid #e8e8e8",
                marginBottom: "0.5rem",
              }}
            >
              <Col span={6}>Name</Col>
              <Col span={4} style={{ textAlign: "right" }}>
                Unit Price
              </Col>
              <Col span={5} style={{ textAlign: "center" }}>
                Qty
              </Col>
              <Col span={2} style={{ textAlign: "right" }}>
                Total Qty
              </Col>
              <Col span={5} style={{ textAlign: "center" }}>
                Total Price
              </Col>
              <Col span={2}></Col>
            </Row>
            <Scrollbars style={{ height: "60vh" }}>
              {selectedItems.map((item) => {
                const unitPrice = parseFloat(item.sunitprice) || 0;
                const quantity = parseFloat(item.weight) || 0;
                const totalPrice = unitPrice * quantity;
                const isDecimal = item?.uomData?.[0]?.decimal === "Y";
                return (
                  <Row
                    key={item.id || item.name}
                    style={{
                      padding: "0.5rem 0.9rem",
                      width: "100%",
                      marginBottom: "0.4rem",
                      height: "auto",
                      alignItems: "center",
                    }}
                  >
                    <Col span={6}>
                      <p
                        title={item.name}
                        style={{
                          margin: 0,
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: "#333",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "100%",
                        }}
                      >
                        {item.name}
                      </p>
                    </Col>
                    <Col span={4} style={{ textAlign: "right" }}>
                      <p style={{ margin: 0, fontSize: "0.8rem", color: "#2F3856" }}>{unitPrice.toLocaleString("en-IN")}</p>
                    </Col>
                    <Col span={5} style={{ textAlign: "center" }}>
                      <Input
                        type="text"
                        value={item.rawWeight || item.weight}
                        onChange={(e) => handleQuantityChange(item, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleQuantityChange(item, e.target.value || "0");
                          } else if (!isDecimal && !/[\d]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                            e.preventDefault();
                          } else if (isDecimal && !/[\d.]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onBlur={(e) => {
                          const newValue = e.target.value || "0";
                          if (isDecimal && [".", "0.", "0.0", "0.00"].includes(newValue)) {
                            handleQuantityChange(item, "0");
                          } else {
                            handleQuantityChange(item, newValue);
                          }
                        }}
                        placeholder={isDecimal ? "e.g., 0.001" : "e.g., 1"}
                        style={{ width: "60px", height: "25px", textAlign: "center", fontSize: "0.8rem" }}
                      />
                    </Col>
                    <Col span={2} style={{ textAlign: "right" }}>
                      <p style={{ margin: 0, fontSize: "0.8rem", color: "#2F3856" }}>{quantity}</p>
                    </Col>
                    <Col span={5} style={{ textAlign: "center" }}>
                      <p style={{ margin: 0, fontSize: "0.8rem", color: "#2F3856" }}>{totalPrice.toLocaleString("en-IN")}</p>
                    </Col>
                    <Col span={2} style={{ textAlign: "center" }}>
                      <img src={DeleteIcon} style={{ height: "2.5vh", cursor: "pointer" }} onClick={() => handleDelete(item)} />
                    </Col>
                  </Row>
                );
              })}
            </Scrollbars>
            <Button
              style={{
                position: "absolute",
                bottom: "0.5rem",
                right: "0.5rem",
                backgroundColor: "#2F3856",
                color: "#fff",
                width: "7vw",
                height: "4.5vh",
                borderRadius: "3px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                fontSize: "0.85rem",
              }}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        )}
      </Modal>

      {posConfig?.predefined_weight_buttons === "Y" ? (
        <Modal
          visible={weightPopup && !(selectedProduct?.alternateUom?.length > 0 && selectedProduct?.showAlternateUomInPos === "Y")}
          getContainer={false}
          zIndex={2500}
          closable={false}
          keyboard={false}
          footer={null}
          width="50%"
          centered
          bodyStyle={{ padding: 10, backgroundColor: "#F3F4F9", height: "80vh" }}
        >
          <Row>
            <Col span={24} style={{ paddingBottom: "1vh" }}>
              <img src={selectedProduct.imageurl} style={{ height: "4.5vh", paddingRight: "1vh" }} />
              <span style={{ fontWeight: 500, fontSize: "1.2rem" }}>{selectedProduct.name}</span>
            </Col>
            <Col
              span={12}
              style={{
                textAlign: "left",
                paddingLeft: "0.5vw",
                display: "flex",
                justifyContent: "space-between", // 👈 key change
                alignItems: "center",
              }}
            >
              <span>
                Sale Price: <span style={{ fontWeight: 500, fontSize: "0.9rem" }}>{selectedProduct.sunitprice}</span>
              </span>

              {posConfig?.showStoreSpecificStock === "Y" && (
                <span>
                  Stock: <span style={{ fontWeight: 500, fontSize: "0.9rem" }}>{selectedProduct?.stock ?? 0}</span>
                </span>
              )}
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <img src={Refresh} style={{ cursor: "pointer" }} onClick={() => callGetWeightAPI(selectedProduct)} />
            </Col>
          </Row>

          <Row style={{ padding: "1vh 3.5% 0 3.5%" }} gutter={[16, 16]}>
            <Col span={16}>
              <Row gutter={[8, 6]}>
                <Col span={24} style={{ paddingBottom: "1vh" }}>
                  <Input
                    className="productSearch sm-payment-search totalQtyInput"
                    style={{
                      borderRadius: "5px",
                      height: "8.3vh",
                      textAlign: "right",
                      fontWeight: "500",
                      fontSize: "2.5em",
                      borderColor: "#fff",
                      color: "#2F3856",
                      fontFamily: "Inter",
                    }}
                    value={selectedProduct.rawWeight ? `${selectedProduct.rawWeight} ${unit}` : ""}
                    id="sm-weight-popup-input"
                    ref={weightInputRef}
                    onChange={(e) => handleInputChange(e.target.value.replace(/[^0-9.]/g, ""))}
                    readOnly={Object.keys(selectedProduct).length === 0}
                    placeholder="Enter Quantity"
                    onPressEnter={() => {
                      if (isStrictQty && isZeroWeight) return;
                      const updatedProduct = {
                        ...selectedProduct,
                        weight: Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight,
                        rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
                        unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
                      };
                      setSelectedProduct(updatedProduct);
                      setWeightPopup(false);
                      selectedProductCallback(updatedProduct);
                    }}
                    onKeyDown={(e) => {
                      const isDecimal = selectedProduct?.uomData?.[0]?.decimal === "Y";
                      if (e.key === "Enter") {
                        e.stopPropagation();
                        e.preventDefault();
                        if (isStrictQty && isZeroWeight) return;
                        const updatedProduct = {
                          ...selectedProduct,
                          weight: Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight,
                          rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
                          unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
                        };
                        setSelectedProduct(updatedProduct);
                        setWeightPopup(false);
                        selectedProductCallback(updatedProduct);
                      } else if (e.key === "Backspace") {
                        e.preventDefault();
                        handleTotalQty("x");
                      } else if (!isDecimal && !/[\d]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                        e.preventDefault();
                      } else if (isDecimal && !/[\d.]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Col>
                <Col span={6}>
                  <button
                    id="sm-amount-button1"
                    className="amt-dial-btn-production"
                    onClick={() => handleTotalQty("1")}
                    style={{ width: "100%", height: "10vh", border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1.2rem" }}
                  >
                    1
                  </button>
                </Col>
                <Col span={6}>
                  <button
                    id="sm-amount-button2"
                    className="amt-dial-btn-production"
                    onClick={() => handleTotalQty("2")}
                    style={{ width: "100%", height: "10vh", border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1.2rem" }}
                  >
                    2
                  </button>
                </Col>
                <Col span={6}>
                  <button
                    id="sm-amount-button3"
                    className="amt-dial-btn-production"
                    onClick={() => handleTotalQty("3")}
                    style={{ width: "100%", height: "10vh", border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1.2rem" }}
                  >
                    3
                  </button>
                </Col>
                <Col span={6}>
                  <button
                    className="amt-dial-btn-production"
                    id="sm-amount-buttonRemove"
                    onClick={() => handleTotalQty("x")}
                    style={{ width: "100%", height: "10vh", border: "1px solid #d9d9d9", borderRadius: "5px" }}
                  >
                    <img src={CancelArrow} alt="back space" width={25} height={15} />
                  </button>
                </Col>
                <Col span={6}>
                  <button
                    id="sm-amount-button4"
                    className="amt-dial-btn-production"
                    onClick={() => handleTotalQty("4")}
                    style={{ width: "100%", height: "10vh", border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1.2rem" }}
                  >
                    4
                  </button>
                </Col>
                <Col span={6}>
                  <button
                    id="sm-amount-button5"
                    className="amt-dial-btn-production"
                    onClick={() => handleTotalQty("5")}
                    style={{ width: "100%", height: "10vh", border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1.2rem" }}
                  >
                    5
                  </button>
                </Col>
                <Col span={6}>
                  <button
                    id="sm-amount-button6"
                    className="amt-dial-btn-production"
                    onClick={() => handleTotalQty("6")}
                    style={{ width: "100%", height: "10vh", border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1.2rem" }}
                  >
                    6
                  </button>
                </Col>
                <Col span={6}>
                  <button
                    id="sm-amount-buttonClear"
                    className="amt-dial-btn-production"
                    onClick={() => handleTotalQty("clear")}
                    style={{ width: "100%", height: "10vh", border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1rem" }}
                  >
                    Clear
                  </button>
                </Col>
                <Col span={18}>
                  <Row gutter={[8, 6]}>
                    <Col span={8}>
                      <button
                        id="sm-amount-button7"
                        className="amt-dial-btn-production"
                        onClick={() => handleTotalQty("7")}
                        style={{ width: "100%", height: "10vh", border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1.2rem" }}
                      >
                        7
                      </button>
                    </Col>
                    <Col span={8}>
                      <button
                        id="sm-amount-button8"
                        className="amt-dial-btn-production"
                        onClick={() => handleTotalQty("8")}
                        style={{ width: "100%", height: "10vh", border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1.2rem" }}
                      >
                        8
                      </button>
                    </Col>
                    <Col span={8}>
                      <button
                        id="sm-amount-button9"
                        className="amt-dial-btn-production"
                        onClick={() => handleTotalQty("9")}
                        style={{ width: "100%", height: "10vh", border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1.2rem" }}
                      >
                        9
                      </button>
                    </Col>
                    <Col span={8}>
                      <button
                        id="sm-amount-button0"
                        className="amt-dial-btn-production"
                        onClick={() => handleTotalQty("0")}
                        style={{ width: "100%", height: "10vh", border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1.2rem" }}
                      >
                        0
                      </button>
                    </Col>
                    <Col span={8}>
                      <button
                        id="sm-amount-button00"
                        className="amt-dial-btn-production"
                        onClick={() => handleTotalQty("00")}
                        style={{ width: "100%", height: "10vh", border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1rem" }}
                      >
                        00
                      </button>
                    </Col>
                    <Col span={8}>
                      <button
                        id="sm-amount-button."
                        className="amt-dial-btn-production"
                        onClick={() => handleTotalQty(".")}
                        style={{ width: "100%", height: "10vh", border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1.2rem" }}
                      >
                        .
                      </button>
                    </Col>
                  </Row>
                </Col>
                <Col span={6}>
                  <button
                    id="sm-amount-buttonEnter"
                    className="amt-dial-btn-production-ent"
                    onClick={() => {
                      if (isStrictQty && isZeroWeight) return;
                      const updatedProduct = {
                        ...selectedProduct,
                        weight: Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight,
                        rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
                        unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
                      };
                      setSelectedProduct(updatedProduct);
                      setWeightPopup(false);
                      selectedProductCallback(updatedProduct);
                    }}
                    style={{
                      width: "100%",
                      height: "20.5vh",
                      backgroundColor: "#2F3856",
                      color: "#fff",
                      borderRadius: "5px",
                      fontSize: "1.2rem",
                      cursor: isStrictQty && isZeroWeight ? "not-allowed" : "pointer",
                    }}
                  >
                    Enter
                  </button>
                </Col>
              </Row>
            </Col>
            <Col span={8}>
              <Row gutter={[8, 8]}>
                <Col span={24}>
                  <div style={{ backgroundColor: "#fff", padding: "1vh", borderRadius: "5px", border: "1px solid #d9d9d9" }}>
                    <h4 style={{ fontSize: "0.9rem", fontWeight: 600, margin: "0 0 0.5vh 0" }}>Unit</h4>
                    <Button
                      style={{
                        width: "100%",
                        height: "5vh",
                        marginBottom: "0.5vh",
                        backgroundColor: unit === "kg" ? "#2F3856" : "#fff",
                        color: unit === "kg" ? "#fff" : "#2F3856",
                        border: "1px solid #d9d9d9",
                      }}
                      onClick={() => {
                        setUnit("kg");
                        handleTotalQty("clear");
                      }}
                    >
                      Kilograms
                    </Button>
                    <Button
                      style={{
                        width: "100%",
                        height: "5vh",
                        marginBottom: "0.5vh",
                        backgroundColor: unit === "g" ? "#2F3856" : "#fff",
                        color: unit === "g" ? "#fff" : "#2F3856",
                        border: "1px solid #d9d9d9",
                      }}
                      onClick={() => {
                        setUnit("g");
                        handleTotalQty("clear");
                      }}
                    >
                      Grams
                    </Button>
                  </div>
                </Col>
                <Col span={24}>
                  <div style={{ backgroundColor: "#fff", padding: "1vh", borderRadius: "5px", border: "1px solid #d9d9d9", maxHeight: "36vh", overflowY: "auto" }}>
                    <h4 style={{ fontSize: "0.9rem", fontWeight: 600, margin: "0 0 0.5vh 0" }}>Quick Select</h4>
                    <Row gutter={[8, 8]}>
                      {frequentWeights.map((weight) => (
                        <Col span={12} key={weight}>
                          <Button style={{ width: "100%", height: "4vh", fontSize: "0.85rem", border: "1px solid #d9d9d9" }} onClick={() => handleFrequentWeight(weight)}>
                            {weight} {unit}
                          </Button>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row style={{ padding: "3% 3.5% 0 3.5%" }} gutter={[8, 6]}>
            <Col span={12}>
              <Button
                onClick={() => {
                  selectedProductCallback(null);
                  setWeightPopup(false);
                }}
                style={{ width: "100%", height: "7vh", borderRadius: "5px", backgroundColor: "#fff", color: "#2F3856" }}
              >
                Cancel
              </Button>
            </Col>
            <Col span={12}>
              <Button
                onClick={() => {
                  if (isStrictQty && isZeroWeight) return;
                  const updatedProduct = {
                    ...selectedProduct,
                    weight: Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight,
                    rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
                    unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
                  };
                  setSelectedProduct(updatedProduct);
                  setWeightPopup(false);
                  selectedProductCallback(updatedProduct);
                }}
                style={{
                  width: "100%",
                  height: "7vh",
                  borderRadius: "5px",
                  backgroundColor: "#2F3856",
                  color: "#fff",
                  cursor: isStrictQty && isZeroWeight ? "not-allowed" : "pointer",
                }}
              >
                Confirm
              </Button>
            </Col>
          </Row>
        </Modal>
      ) : (
        <Modal
          visible={weightPopup && !(selectedProduct?.alternateUom?.length > 0 && selectedProduct?.showAlternateUomInPos === "Y")}
          getContainer={false}
          zIndex={2500}
          closable={false}
          keyboard={false}
          footer={null}
          width="30%"
          centered
          bodyStyle={{ padding: 10, backgroundColor: "#F3F4F9" }}
        >
          <Row>
            <Col span={12} style={{ textAlign: "left", paddingLeft: "0.5vw" }}></Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <img src={Refresh} style={{ cursor: "pointer" }} onClick={() => callGetWeightAPI(selectedProduct)} />
            </Col>
          </Row>

          <Row style={{ padding: "0 3.5% 0 3.5%" }} gutter={[8, 6]}>
            <Col span={24} style={{ paddingBottom: "1vh" }}>
              <img src={selectedProduct.imageurl} style={{ height: "4.5vh" }} /> <span style={{ fontWeight: 500, fontSize: "1rem" }}>{selectedProduct.name}</span>
            </Col>
            <Col
              span={24}
              style={{
                paddingBottom: "1vh",
                display: "flex",
                justifyContent: "space-between", // 👈 key change
                alignItems: "center",
              }}
            >
              <span>
                Sale Price: <span style={{ fontWeight: 500, fontSize: "0.9rem" }}>{selectedProduct.sunitprice}</span>
              </span>

              {posConfig?.showStoreSpecificStock === "Y" && (
                <span>
                  Stock: <span style={{ fontWeight: 500, fontSize: "0.9rem" }}>{selectedProduct?.stock ?? 0}</span>
                </span>
              )}
            </Col>
          </Row>

          <Row style={{ padding: "0 3.5% 0 3.5%" }} gutter={[8, 6]}>
            <Col span={24} style={{ paddingBottom: "2vh" }}>
              <Input
                className="productSearch sm-payment-search totalQtyInput"
                style={{
                  borderRadius: "0.5vw",
                  height: "8.3vh",
                  textAlign: "right",
                  fontWeight: "500",
                  fontSize: "2.5em",
                  borderColor: "#fff",
                  color: "#2F3856",
                  fontFamily: "Inter",
                }}
                disabled={!isAdvanceOrderEnabled && selectedProduct?.restrictPosQtyEdit === "Y"}
                value={selectedProduct.rawWeight || selectedProduct.weight || ""}
                id="sm-weight-popup-input"
                ref={weightInputRef}
                onChange={(e) => handleInputChange(e.target.value)}
                readOnly={Object.keys(selectedProduct).length === 0}
                placeholder="Enter Quantity"
                onPressEnter={() => {
                  if (isStrictQty && isZeroWeight) return;
                  const updatedProduct = {
                    ...selectedProduct,
                    weight: Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight,
                    rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
                    unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
                  };
                  setSelectedProduct(updatedProduct);
                  setWeightPopup(false);
                  selectedProductCallback(updatedProduct);
                }}
                onKeyDown={(e) => {
                  const isDecimal = selectedProduct?.uomData?.[0]?.decimal === "Y";
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    e.preventDefault();
                    if (isStrictQty && isZeroWeight) return;
                    const updatedProduct = {
                      ...selectedProduct,
                      weight: Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight,
                      rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
                      unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
                    };
                    setSelectedProduct(updatedProduct);
                    setWeightPopup(false);
                    selectedProductCallback(updatedProduct);
                  } else if (e.key === "Backspace") {
                    e.preventDefault();
                    handleTotalQty("x");
                  } else if (!isDecimal && !/[\d]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                    e.preventDefault();
                  } else if (isDecimal && !/[\d.]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Col>
            <Col span={6}>
              <button id="sm-amount-button1" className="amt-dial-btn-production" onClick={() => handleTotalQty("1")}>
                1
              </button>
            </Col>
            <Col span={6}>
              <button id="sm-amount-button2" className="amt-dial-btn-production" onClick={() => handleTotalQty("2")}>
                2
              </button>
            </Col>
            <Col span={6}>
              <button id="sm-amount-button3" className="amt-dial-btn-production" onClick={() => handleTotalQty("3")}>
                3
              </button>
            </Col>
            <Col span={6}>
              <button className="amt-dial-btn-production" id="sm-amount-buttonRemove" onClick={() => handleTotalQty("x")}>
                <img src={CancelArrow} alt="back space" width={25} height={15} />
              </button>
            </Col>
            <Col span={6}>
              <button id="sm-amount-button4" className="amt-dial-btn-production" onClick={() => handleTotalQty("4")}>
                4
              </button>
            </Col>
            <Col span={6}>
              <button id="sm-amount-button5" className="amt-dial-btn-production" onClick={() => handleTotalQty("5")}>
                5
              </button>
            </Col>
            <Col span={6}>
              <button id="sm-amount-button6" className="amt-dial-btn-production" onClick={() => handleTotalQty("6")}>
                6
              </button>
            </Col>
            <Col span={6}>
              <button id="sm-amount-buttonClear" className="amt-dial-btn-production" onClick={() => handleTotalQty("clear")}>
                Clear
              </button>
            </Col>
            <Col span={18}>
              <Row gutter={[8, 6]}>
                <Col span={8}>
                  <button id="sm-amount-button7" className="amt-dial-btn-production" onClick={() => handleTotalQty("7")}>
                    7
                  </button>
                </Col>
                <Col span={8}>
                  <button id="sm-amount-button8" className="amt-dial-btn-production" onClick={() => handleTotalQty("8")}>
                    8
                  </button>
                </Col>
                <Col span={8}>
                  <button id="sm-amount-button9" className="amt-dial-btn-production" onClick={() => handleTotalQty("9")}>
                    9
                  </button>
                </Col>
                <Col span={8}>
                  <button id="sm-amount-button0" className="amt-dial-btn-production" onClick={() => handleTotalQty("0")}>
                    0
                  </button>
                </Col>
                <Col span={8}>
                  <button id="sm-amount-button00" className="amt-dial-btn-production" onClick={() => handleTotalQty("00")}>
                    00
                  </button>
                </Col>
                <Col span={8}>
                  <button id="sm-amount-button." className="amt-dial-btn-production" onClick={() => handleTotalQty(".")}>
                    .
                  </button>
                </Col>
              </Row>
            </Col>
            <Col span={6}>
              <button
                id="sm-amount-buttonEnter"
                className="amt-dial-btn-production-ent"
                onClick={() => {
                  if (isStrictQty && isZeroWeight) return;
                  const updatedProduct = {
                    ...selectedProduct,
                    weight: Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight,
                    rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
                    unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
                  };
                  setSelectedProduct(updatedProduct);
                  setWeightPopup(false);
                  selectedProductCallback(updatedProduct);
                }}
              >
                Enter
              </button>
            </Col>
          </Row>

          <Row style={{ padding: "3% 3.5% 0 3.5%" }} gutter={[8, 6]}>
            <Col span={12}>
              <Button
                onClick={() => {
                  selectedProductCallback(null);
                  setWeightPopup(false);
                }}
                style={{ width: "100%", height: "7vh", borderRadius: "5px", backgroundColor: "#fff", color: "#2F3856" }}
              >
                Cancel
              </Button>
            </Col>
            <Col span={12}>
              <Button
                onClick={() => {
                  if (isStrictQty && isZeroWeight) return;
                  const updatedProduct = {
                    ...selectedProduct,
                    weight: Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight,
                    rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
                    unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
                  };
                  setSelectedProduct(updatedProduct);
                  setWeightPopup(false);
                  selectedProductCallback(updatedProduct);
                }}
                style={{
                  width: "100%",
                  height: "7vh",
                  borderRadius: "5px",
                  backgroundColor: "#2F3856",
                  color: "#fff",
                  cursor: isStrictQty && isZeroWeight ? "not-allowed" : "pointer",
                }}
              >
                Confirm
              </Button>
            </Col>
          </Row>
        </Modal>
      )}
    </>
  );
};

export default ProductsPopup;
